// frontend/src/features/landingPages/api/landingPagesApi.js
//
// M12 Landing Pages API client (/api/v1/landing-pages).
// The server is authoritative for workspace membership, permissions,
// validation, versioning and approval. This client only transports requests.

import axiosInstance from '../../../api/axios';

const BASE = '/v1/landing-pages';

const withWorkspace = (workspaceId, extraHeaders = {}) => ({
  headers: {
    ...(workspaceId ? { 'x-workspace-id': workspaceId } : {}),
    ...extraHeaders,
  },
});

const ifMatch = (rowVersion) =>
  rowVersion === undefined || rowVersion === null ? {} : { 'If-Match': `"${rowVersion}"` };

/** Normalized error: { status, code, message, details, requestId } */
export function toApiError(error) {
  const status = error?.response?.status ?? 0;
  const body = error?.response?.data || {};
  const err = body.error && typeof body.error === 'object' ? body.error : {};
  return {
    status,
    code: err.code || (status ? `HTTP_${status}` : 'NETWORK_ERROR'),
    message:
      err.message ||
      (status ? 'The request could not be completed.' : 'Could not reach the server.'),
    details: Array.isArray(err.details) ? err.details : [],
    requestId: body.requestId || null,
  };
}

/** A fresh key per user action; retries of the same action reuse it. */
export function newIdempotencyKey() {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `lp-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function isFeatureDisabled(apiError) {
  return apiError?.status === 404 && apiError?.code === 'FEATURE_DISABLED';
}

const landingPagesApi = {
  async catalog(workspaceId) {
    const res = await axiosInstance.get(`${BASE}/catalog`, withWorkspace(workspaceId));
    return res.data?.data;
  },

  async list(workspaceId, { limit = 50, cursor } = {}) {
    const res = await axiosInstance.get(BASE, {
      ...withWorkspace(workspaceId),
      params: { limit, cursor: cursor || undefined },
    });
    return res.data?.data; // { items, nextCursor, access }
  },

  async get(workspaceId, pageId) {
    const res = await axiosInstance.get(
      `${BASE}/${encodeURIComponent(pageId)}`,
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  async create(workspaceId, { name, sections }) {
    const res = await axiosInstance.post(BASE, { name, sections }, withWorkspace(workspaceId));
    return res.data?.data;
  },

  async saveDraft(workspaceId, pageId, { name, sections }, rowVersion) {
    const res = await axiosInstance.put(
      `${BASE}/${encodeURIComponent(pageId)}/draft`,
      { name, sections },
      withWorkspace(workspaceId, ifMatch(rowVersion))
    );
    return res.data?.data;
  },

  async submit(workspaceId, pageId, versionId, rowVersion) {
    const res = await axiosInstance.post(
      `${BASE}/${encodeURIComponent(pageId)}/versions/${encodeURIComponent(versionId)}/submit`,
      {},
      withWorkspace(workspaceId, ifMatch(rowVersion))
    );
    return res.data?.data;
  },

  async approve(workspaceId, pageId, versionId, contentHash, reason) {
    const res = await axiosInstance.post(
      `${BASE}/${encodeURIComponent(pageId)}/versions/${encodeURIComponent(versionId)}/approve`,
      { contentHash, reason: reason || undefined },
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  async reject(workspaceId, pageId, versionId, contentHash, reason) {
    const res = await axiosInstance.post(
      `${BASE}/${encodeURIComponent(pageId)}/versions/${encodeURIComponent(versionId)}/reject`,
      { contentHash, reason },
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  /** Publish the approved version to the workspace CMS (idempotent per key). */
  async publish(workspaceId, pageId, idempotencyKey) {
    const res = await axiosInstance.post(
      `${BASE}/${encodeURIComponent(pageId)}/publish`,
      {},
      withWorkspace(workspaceId, { 'Idempotency-Key': idempotencyKey })
    );
    return res.data?.data;
  },

  /** Re-publish the previously live version to the same CMS page. */
  async rollback(workspaceId, pageId, idempotencyKey) {
    const res = await axiosInstance.post(
      `${BASE}/${encodeURIComponent(pageId)}/rollback`,
      {},
      withWorkspace(workspaceId, { 'Idempotency-Key': idempotencyKey })
    );
    return res.data?.data;
  },

  /** Choose who receives this page's form leads (null = the page creator). */
  async updateSettings(workspaceId, pageId, { leadOwnerId }) {
    const res = await axiosInstance.put(
      `${BASE}/${encodeURIComponent(pageId)}/settings`,
      { leadOwnerId: leadOwnerId || null },
      withWorkspace(workspaceId)
    );
    return res.data?.data;
  },

  /** Active workspace members (for the lead owner picker). */
  async members(workspaceId) {
    const res = await axiosInstance.get(`/workspace/${encodeURIComponent(workspaceId)}/members`);
    return Array.isArray(res.data?.members) ? res.data.members : [];
  },

  /** Server-rendered, escaped preview HTML (string). */
  async previewHtml(workspaceId, pageId, versionId) {
    const res = await axiosInstance.get(
      `${BASE}/${encodeURIComponent(pageId)}/versions/${encodeURIComponent(versionId)}/preview`,
      withWorkspace(workspaceId)
    );
    return res.data?.data?.html || '';
  },
};

export default landingPagesApi;
